import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { inspectElement } from '~/lib/FormCssApi';
import * as Toast from '~/lib/Toast';
import {
    completePick,
    setInspectorReady,
    stopPickingElement,
    useIsPickingElement,
} from '~/lib/CssInspectorState';

// Under the styles panel, which is z-10, so an element that runs behind the
//  panel doesn't get highlighted over the top of it
const OVERLAY_Z_INDEX = 9;

// Roughly a three line label. Only decides whether there is room above the
//  element, since the label positions itself off its own height
const LABEL_CLEARANCE = 76;

// A fixed id so this can only ever be on screen once. Raising it is tied to a
//  React effect, and an effect run more than once — remounted, or a second
//  inspector somewhere on the page — would otherwise stack a message per run
const PICK_TOAST_ID = 'css-inspector-pick-an-element';

/**
 * Outlines whatever the mouse is over in the form designer and names the
 *  selector that reaches it.
 *
 * A list of hook names doesn't say which one is the element someone means —
 *  that is what sent an earlier round of custom CSS at the category headings
 *  instead of the blocks beneath them. Pointing at a thing and being told what
 *  to call it removes the guess.
 *
 * Only hovers, deliberately: the designer already uses clicks to open a
 *  question for editing, and intercepting those is a separate decision.
 */
/**
 * The hook under a pointer, but only within the form preview. The designer's
 *  own chrome carries no hooks, so it falls out of closest() on its own.
 */
function inspectorTarget(target) {
    const found = inspectElement(target instanceof Element ? target : null);

    return found && found.element.closest('.breely-form') ? found : null;
}

const CssInspector = () => {
    const [hook, setHook] = useState(null);
    const [rect, setRect] = useState(null);
    const isPicking = useIsPickingElement();

    // Tells the editor there is a form on screen to point at. Without it the
    //  editor would offer to pick from a tab that isn't showing one
    useEffect(() => {
        setInspectorReady(true);

        return () => setInspectorReady(false);
    }, []);

    const clear = useCallback(() => {
        setHook(null);
        setRect(null);
    }, []);

    useEffect(() => {
        function onMouseOver(event) {
            const found = inspectorTarget(event.target);

            if (!found) {
                clear();

                return;
            }

            setHook(found);
            setRect(found.element.getBoundingClientRect());
        }

        function onMouseOut(event) {
            if (!event.relatedTarget) {
                // Left the window entirely
                clear();
            }
        }

        document.addEventListener('mouseover', onMouseOver);
        document.addEventListener('mouseout', onMouseOut);

        return () => {
            document.removeEventListener('mouseover', onMouseOver);
            document.removeEventListener('mouseout', onMouseOut);
        };
    }, [clear]);

    useEffect(() => {
        if (!isPicking) {
            return;
        }

        document.body.classList.add('is-picking-css');

        // The styles panel slides away while picking, taking its instructions
        //  with it, so they follow the pointer to the form instead
        Toast.info(
            'Click an element to style it, or press Esc to cancel.',
            { autoClose: false, toastId: PICK_TOAST_ID },
        );

        // Everything is taken in the capture phase: a click in the designer
        //  opens a question for editing, and a pointerdown starts a drag, so
        //  both have to be stopped before they reach the form
        function swallow(event) {
            if (!inspectorTarget(event.target)) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
        }

        function onClick(event) {
            const found = inspectorTarget(event.target);

            if (!found) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            completePick({
                selector: found.selector,
                inQuestionType: found.inQuestionType,
                inQuestion: found.inQuestion,
            });
        }

        function onKeyDown(event) {
            if (event.key === 'Escape') {
                stopPickingElement();
            }
        }

        document.addEventListener('pointerdown', swallow, true);
        document.addEventListener('mousedown', swallow, true);
        document.addEventListener('click', onClick, true);
        document.addEventListener('keydown', onKeyDown);

        return () => {
            Toast.dismiss(PICK_TOAST_ID);
            document.body.classList.remove('is-picking-css');
            document.removeEventListener('pointerdown', swallow, true);
            document.removeEventListener('mousedown', swallow, true);
            document.removeEventListener('click', onClick, true);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [isPicking]);

    useEffect(() => {
        if (!hook) {
            return;
        }

        function reposition() {
            if (!hook.element.isConnected) {
                clear();

                return;
            }

            setRect(hook.element.getBoundingClientRect());
        }

        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);

        return () => {
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
        };
    }, [hook, clear]);

    if (!hook || !rect || (rect.width === 0 && rect.height === 0)) {
        return null;
    }

    // Above the element unless it's near the top of the window, where there is
    //  nowhere to put the label
    const labelBelow = rect.top < LABEL_CLEARANCE;
    const narrower = [
        { caption: 'in this question type', selector: hook.inQuestionType },
        { caption: 'in this question', selector: hook.inQuestion },
    ].filter(option => !!option.selector);

    return createPortal(
        <div
            className="pointer-events-none fixed inset-0"
            style={{ zIndex: OVERLAY_Z_INDEX }}
            aria-hidden="true"
        >
            <div
                className="absolute border-2 border-primary-700 rounded-sm bg-primary-700/5"
                style={{
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height,
                }}
            />
            <div
                className="absolute bg-primary-700 text-white text-xs px-2 py-1 rounded shadow-lg"
                style={{
                    top: labelBelow ? rect.bottom + 4 : rect.top - 4,
                    left: rect.left,
                    // Wraps rather than running off the side of the window
                    maxWidth: `calc(100vw - ${Math.max(rect.left, 0)}px - 16px)`,
                    // Lets the label sit on its own height, whatever it is
                    transform: labelBelow ? undefined : 'translateY(-100%)',
                }}
            >
                <div className="font-mono break-all">{hook.selector}</div>

                {narrower.map(option => (
                    <div
                        key={option.selector}
                        className="font-mono break-all opacity-70 mt-0.5"
                    >
                        {option.selector}
                        <span className="font-sans ml-2 opacity-80">
                            {option.caption}
                        </span>
                    </div>
                ))}

                {isPicking && (
                    <div className="font-sans opacity-75 mt-1">
                        click to add the first · esc to cancel
                    </div>
                )}
            </div>
        </div>,
        document.body,
    );
};

export default CssInspector;
